import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  Autocomplete,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress as MUICircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useInventory } from '../../contexts/InventoryContext';
import { useOrder } from '../../contexts/OrderContext';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../utils/api';

const EditOrderModal = ({ open, onClose, order, onUpdated }) => {
  const { items: inventoryItems, loadInventory } = useInventory();
  const { updateOrder } = useOrder();
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id || selectedLocation?._id || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form state
  const [customer, setCustomer] = useState({ type: 'individual' });
  const [items, setItems] = useState([]);
  const [manualItem, setManualItem] = useState({ name: '', sku: '', quantity: 1, unitPrice: 0, notes: '' });
  const [shippingAddress, setShippingAddress] = useState({});
  const [payment, setPayment] = useState({});
  const [delivery, setDelivery] = useState({});
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [orderStatus, setOrderStatus] = useState('PENDING');
  // Per-location pricing options and selections (to mirror NewOrderModal)
  const [pricingOptions, setPricingOptions] = useState({ shipping: [], warranties: [], taxRate: 0 });
  const [selectedShippingTitle, setSelectedShippingTitle] = useState('');
  const [selectedWarrantyTitle, setSelectedWarrantyTitle] = useState('');
  // Custom fee state
  const [customFee, setCustomFee] = useState('');
  const [customFeeLabel, setCustomFeeLabel] = useState('');
  const [confirmFullPayment, setConfirmFullPayment] = useState(false);
  const [recordPaymentAmount, setRecordPaymentAmount] = useState('');
  const [recordPaymentReference, setRecordPaymentReference] = useState('');
  const [paidAmount, setPaidAmount] = useState(0);
  // Live SKU search state (external products)
  const [skuQuery, setSkuQuery] = useState('');
  const [skuResults, setSkuResults] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuError, setSkuError] = useState('');

  // Load inventory and populate form when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadInventory();
    // Load pricing options for the selected location (shipping & warranties)
    if (locationId) {
      api.get(`/locations/${locationId}/pricing`)
        .then(res => {
          if (cancelled) return;
          const pricing = res?.data?.data?.pricing || { shipping: [], warranties: [], taxRate: 0 };
          setPricingOptions(pricing);
          // After options exist, re-apply saved titles so <select> values match persisted order.pricing
          const selectedExtras = order?.pricing?.selectedExtras || {};
          const prevShip = Array.isArray(selectedExtras.shipping) ? (selectedExtras.shipping[0] || '') : '';
          const prevWarranty = Array.isArray(selectedExtras.warranties) ? (selectedExtras.warranties[0] || '') : '';
          setSelectedShippingTitle(prevShip);
          setSelectedWarrantyTitle(prevWarranty);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Failed to load pricing options', err);
          setPricingOptions({ shipping: [], warranties: [], taxRate: 0 });
        });
    }
    if (order) {
        setCustomer(order.customer || { type: 'individual', firstName: '', lastName: '', businessName: '', email: '', phone: '' });
        // Normalize items from various possible shapes
        const candidates = [
          Array.isArray(order.items) ? order.items : [],
          Array.isArray(order.orderItems) ? order.orderItems : [],
          Array.isArray(order.lineItems) ? order.lineItems : [],
          Array.isArray(order.products) ? order.products : [],
        ].find(arr => arr.length > 0) || [];

        const normalized = candidates.map((it) => {
          // Some APIs nest item under it.item or it.product
          const n = it?.item || it?.product || it;
          const inv = n.inventory || it.inventory;
          const prod = n.product || it.product;
          const base = n;

          const qty = Number(base.quantity ?? base.qty ?? it.quantity ?? it.qty ?? 1) || 1;
          const unit = Number(base.unitPrice ?? base.price ?? it.unitPrice ?? it.price ?? 0) || 0;
          const total = Number(base.totalPrice ?? it.totalPrice ?? (unit * qty)) || (unit * qty);

          const name = base.name ?? inv?.name ?? prod?.name ?? it.name ?? '';
          const sku = base.sku ?? inv?.sku ?? prod?.sku ?? it.sku ?? '';
          const inventoryId = base.inventoryId ?? inv?._id ?? inv?.id ?? it.inventoryId ?? null;

          return {
            id: base.id || it.id || null,
            inventoryId,
            name,
            sku,
            quantity: qty,
            unitPrice: unit,
            totalPrice: total,
            notes: base.notes || it.notes || '',
            arrived: base.arrived || it.arrived || false,
            arrivedAt: base.arrivedAt || it.arrivedAt || null
          };
        });

        setItems(normalized);
        setShippingAddress(order.shippingAddress || { street: '', city: '', state: '', zipCode: '', country: 'USA' });
        // Payment method is stored directly on order as paymentMethod, not in a nested payment object
        // Payment status from backend is uppercase, convert to lowercase for dropdown
        const rawPaymentStatus = (order.paymentStatus || order.payment?.status || 'pending').toString().toLowerCase();
        // Map backend values to dropdown values
        const paymentStatusMap = {
          'pending': 'pending',
          'paid': 'paid',
          'partially_paid': 'partially_paid',
          'failed': 'failed',
          'refunded': 'refunded'
        };
        setPayment({
          method: order.paymentMethod || order.payment?.method || 'CASH',
          status: paymentStatusMap[rawPaymentStatus] || 'pending'
        });
        // Set paid amount from order
        const orderPaidAmount = Number(order.paidAmount || 0);
        const orderTotalAmount = Number(order.totalAmount || 0);
        setPaidAmount(orderPaidAmount);
        // If already fully paid, set the checkbox as checked
        if (orderPaidAmount >= orderTotalAmount && orderTotalAmount > 0) {
          setConfirmFullPayment(true);
        } else {
          setConfirmFullPayment(false);
        }
        // Delivery type is stored as shippingMethod on the order
        const deliveryType = (order.shippingMethod || order.delivery?.type || 'pickup').toString().toLowerCase();
        setDelivery({ type: deliveryType, notes: order.delivery?.notes || '' });
        // Use the backend status directly (uppercase enum values)
        const raw = String(order.status || 'PENDING').toUpperCase();
        // Map legacy values to new ones
        const legacyMap = {
          'CONFIRMED': 'COMPLETE',
          'SHIPPED': 'ITEMS_ARRIVED',
          'COMPLETED': 'COMPLETE'
        };
        setOrderStatus(legacyMap[raw] || raw);
        // Initialize selected extras from existing order pricing if present
        const selectedExtras = order?.pricing?.selectedExtras || {};
        const prevShip = Array.isArray(selectedExtras.shipping) ? (selectedExtras.shipping[0] || '') : '';
        const prevWarranty = Array.isArray(selectedExtras.warranties) ? (selectedExtras.warranties[0] || '') : '';
        setSelectedShippingTitle(prevShip);
        setSelectedWarrantyTitle(prevWarranty);
        // Load custom fee from existing order
        setCustomFee(order.customFee ? String(order.customFee) : '');
        setCustomFeeLabel(order.customFeeLabel || '');
        setNotes(order.notes || '');
        setInternalNotes(order.internalNotes || '');
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order, locationId]);
  
  // Add inventory item to order
  const addInventoryItem = (inventoryItem, quantity = 1) => {
    if (!inventoryItem) return;
    
    const newItem = {
      inventoryId: inventoryItem._id,
      name: inventoryItem.name,
      sku: inventoryItem.sku,
      quantity: quantity,
      unitPrice: inventoryItem.pricing?.retail || 0,
      totalPrice: (inventoryItem.pricing?.retail || 0) * quantity,
      notes: ''
    };
    
    setItems([...items, newItem]);
  };
  
  // Add manual item to order
  const addManualItem = () => {
    if (!manualItem.name || manualItem.quantity < 1 || manualItem.unitPrice <= 0) return;
    
    const newItem = {
      inventoryId: null,
      name: manualItem.name,
      sku: manualItem.sku || 'MANUAL-' + Date.now(),
      quantity: manualItem.quantity,
      unitPrice: manualItem.unitPrice,
      totalPrice: manualItem.unitPrice * manualItem.quantity,
      notes: manualItem.notes
    };
    
    setItems([...items, newItem]);
    
    // Reset manual item form
    setManualItem({ name: '', sku: '', quantity: 1, unitPrice: 0, notes: '' });
  };
  
  // Remove item from order
  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };
  
  // Update item quantity
  const updateItemQuantity = (index, quantity) => {
    if (quantity < 1) return;
    
    const newItems = [...items];
    newItems[index].quantity = quantity;
    newItems[index].totalPrice = newItems[index].unitPrice * quantity;
    setItems(newItems);
  };
  
  // Update item arrived status
  const updateItemArrived = (index, arrived) => {
    const newItems = [...items];
    newItems[index].arrived = arrived;
    newItems[index].arrivedAt = arrived ? new Date().toISOString() : null;
    setItems(newItems);
  };
  
  // Debounced search for SKU field (3+ chars)
  useEffect(() => {
    const q = (manualItem.sku || '').trim();
    setSkuQuery(q);
    setSkuError('');
    if (!locationId) {
      setSkuResults([]);
      return;
    }
    if (!q || q.length < 3) {
      setSkuResults([]);
      return;
    }
    let cancelled = false;
    setSkuLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          locationId,
          provider: 'ashley',
          search: q,
          page: '1',
          limit: '10'
        });
        const res = await api.get(`/integrations/external-products/search?${params.toString()}`);
        if (cancelled) return;
        const data = res?.data?.data || {};
        setSkuResults(Array.isArray(data.products) ? data.products : []);
      } catch (err) {
        if (!cancelled) setSkuError(err?.response?.data?.message || err?.message || 'Search failed');
      } finally {
        if (!cancelled) setSkuLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualItem.sku, locationId]);
  
  // Add external product from SKU search to items
  const addExternalProductToItems = (product) => {
    const price = Number(product.salePrice ?? product.wholesalePrice ?? product.msrpPrice ?? 0);
    const newItem = {
      id: null,
      inventoryId: null,
      name: product.name || 'External Item',
      sku: product.sku || '',
      quantity: 1,
      unitPrice: price,
      totalPrice: price,
      notes: '',
      arrived: false,
      arrivedAt: null
    };
    setItems(prev => [...prev, newItem]);
    // Reset manual item form
    setManualItem({ name: '', sku: '', quantity: 1, unitPrice: 0, notes: '' });
    setSkuResults([]);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!order) return;
    
    // If payment status is 'paid' and checkbox is checked, include flag to mark as fully paid
    const paymentData = { ...payment };
    if (payment.status === 'paid' && confirmFullPayment) {
      paymentData.markFullyPaid = true;
      // Include payment amount and reference for creating a payment record
      paymentData.paymentAmount = recordPaymentAmount ? Number(recordPaymentAmount) : totals.totalAmount;
      paymentData.paymentReference = recordPaymentReference || 'Full payment recorded';
    } else if (recordPaymentAmount && Number(recordPaymentAmount) > 0) {
      // Record a partial payment
      paymentData.paymentAmount = Number(recordPaymentAmount);
      paymentData.paymentReference = recordPaymentReference || 'Payment recorded';
    }
    
    try {
      const orderData = {
        customer,
        items: items.map(item => ({
          id: item.id,
          inventoryId: item.inventoryId,
          name: item.name || '',
          sku: item.sku || '',
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          notes: item.notes,
          arrived: item.arrived || false,
          arrivedAt: item.arrivedAt || null
        })),
        shippingAddress,
        payment: paymentData,
        delivery,
        notes,
        internalNotes,
        status: orderStatus,
        pricing: {
          selectedExtras: {
            shipping: (delivery.type !== 'pickup' && selectedShippingTitle) ? [selectedShippingTitle] : [],
            warranties: selectedWarrantyTitle ? [selectedWarrantyTitle] : []
          },
          customFee: customFee ? Number(customFee) : 0,
          customFeeLabel: customFeeLabel || null
        }
      };
      
      const updatedOrder = await updateOrder(order.id || order._id, orderData);
      if (onUpdated && updatedOrder) {
        onUpdated(updatedOrder);
      }
      onClose();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };
  
  // Calculate order totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.totalPrice || (item.unitPrice * item.quantity)), 0);
    const taxRateFrac = Number(pricingOptions.taxRate || 0) / 100;
    let shippingExtraAmount = 0;
    let warrantyExtraAmount = 0;
    if (delivery.type !== 'pickup' && selectedShippingTitle) {
      const opt = pricingOptions.shipping.find(s => s.title === selectedShippingTitle);
      if (opt) shippingExtraAmount += Number(opt.cost || 0);
    }
    if (selectedWarrantyTitle) {
      const opt = pricingOptions.warranties.find(w => w.title === selectedWarrantyTitle);
      if (opt) warrantyExtraAmount += Number(opt.cost || 0);
    }
    const discountAmount = 0; // No discount for now
    const customFeeAmount = Number(customFee || 0);
    const taxableBase =
      subtotal + shippingExtraAmount + warrantyExtraAmount + customFeeAmount - discountAmount;
    const taxAmount = Math.round(taxableBase * taxRateFrac * 100) / 100;
    const totalAmount = taxableBase + taxAmount;
    
    return { subtotal, taxAmount, shippingExtraAmount, warrantyExtraAmount, customFeeAmount, discountAmount, totalAmount };
  };
  
  const totals = calculateTotals();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Edit Order - {order?.orderNumber}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Customer Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Customer Type"
                  value={customer.type || 'individual'}
                  onChange={(e) => setCustomer({...customer, type: e.target.value})}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                </TextField>
              </Grid>
              
              {customer.type === 'business' ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Business Name"
                    value={customer.businessName || ''}
                    onChange={(e) => setCustomer({...customer, businessName: e.target.value})}
                    fullWidth
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      value={customer.firstName || ''}
                      onChange={(e) => setCustomer({...customer, firstName: e.target.value})}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      value={customer.lastName || ''}
                      onChange={(e) => setCustomer({...customer, lastName: e.target.value})}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={customer.email || ''}
                  onChange={(e) => setCustomer({...customer, email: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={customer.phone || ''}
                  onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Address Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Street Address"
                  value={shippingAddress.street || ''}
                  onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={shippingAddress.city || ''}
                  onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="State"
                  value={shippingAddress.state || ''}
                  onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ZIP Code"
                  value={shippingAddress.zipCode || ''}
                  onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Country"
                  value={shippingAddress.country || 'USA'}
                  onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Order Items */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Order Items</Typography>
            
            {/* Inventory Item Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={inventoryItems}
                  getOptionLabel={(option) => `${option.name} (${option.sku}) - $${option.pricing?.retail || 0}`}
                  onChange={(event, value) => { if (value) { addInventoryItem(value); }}}
                  renderInput={(params) => <TextField {...params} label="Select Inventory Item" placeholder="Search by name or SKU..." />}
                />
              </Grid>
            </Grid>
            
            {/* Manual Item Entry */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={5}>
                <TextField label="Product Name" value={manualItem.name} onChange={(e) => setManualItem({...manualItem, name: e.target.value})} fullWidth />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="SKU" value={manualItem.sku} onChange={(e) => setManualItem({...manualItem, sku: e.target.value})} fullWidth />
              </Grid>
              {/* Live SKU search results */}
              {skuQuery && skuQuery.length >= 3 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Matches for "{skuQuery}"</Typography>
                      {skuLoading && <MUICircularProgress size={16} />}
                    </Box>
                    {skuError && (
                      <Typography color="error" variant="caption">{skuError}</Typography>
                    )}
                    <List dense>
                      {skuResults.length === 0 && !skuLoading ? (
                        <ListItem>
                          <ListItemText primary="No matches found" />
                        </ListItem>
                      ) : (
                        skuResults.map((p) => (
                          <ListItem key={`${p.catalogId}-${p.externalId}`} secondaryAction={
                            <FormControlLabel
                              control={<Checkbox onChange={(e) => { if (e.target.checked) addExternalProductToItems(p); }} />}
                              label="Add"
                            />
                          }>
                            <ListItemText
                              primary={`${p.name || 'Item'}${p.sku ? ` (SKU: ${p.sku})` : ''}`}
                              secondary={`Price: $${Number(p.salePrice ?? p.wholesalePrice ?? p.msrpPrice ?? 0).toFixed(2)}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                    <Typography variant="caption" color="text.secondary">
                      Source: Ashley external catalog for this location
                    </Typography>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12} sm={2}>
                <TextField label="Quantity" type="number" value={manualItem.quantity} onChange={(e) => setManualItem({...manualItem, quantity: parseInt(e.target.value) || 1})} fullWidth />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Unit Price"
                  type="number"
                  value={manualItem.unitPrice}
                  onChange={(e) => setManualItem({...manualItem, unitPrice: parseFloat(e.target.value) || 0})}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Notes" value={manualItem.notes} onChange={(e) => setManualItem({...manualItem, notes: e.target.value})} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addManualItem} fullWidth>Add Manual Item</Button>
              </Grid>
            </Grid>
            
            {/* Added Items List */}
            {items.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Added Items ({items.length})</Typography></Grid>
                {items.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                          <Typography variant="body2" color="text.secondary">SKU: {item.sku}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => updateItemQuantity(index, item.quantity - 1)}><RemoveIcon /></IconButton>
                            <TextField type="number" value={item.quantity} onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)} sx={{ width: 60, mx: 1 }} size="small" InputProps={{ inputProps: { min: 1 } }} />
                            <IconButton size="small" onClick={() => updateItemQuantity(index, item.quantity + 1)}><AddIcon /></IconButton>
                          </Box>
                          <Typography variant="body1">${(item.unitPrice || 0).toFixed(2)} × {item.quantity} = ${(item.totalPrice || 0).toFixed(2)}</Typography>
                          <IconButton size="small" onClick={() => removeItem(index)}><CloseIcon /></IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={item.arrived || false}
                              onChange={(e) => updateItemArrived(index, e.target.checked)}
                              color="success"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={item.arrived ? 'success.main' : 'text.secondary'}>
                              {item.arrived 
                                ? `Received${item.arrivedAt ? ` on ${new Date(item.arrivedAt).toLocaleDateString()}` : ''}`
                                : 'Mark as received from factory'
                              }
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  </Grid>
                ))}
                {/* Order Totals */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mt: 2 }}>
                    <Grid container spacing={2} sx={{ maxWidth: 300 }}>
                      <Grid item xs={6}><Typography variant="body2">Subtotal:</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2" align="right">${totals.subtotal.toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2">Tax:</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2" align="right">${totals.taxAmount.toFixed(2)}</Typography></Grid>
                      {totals.shippingExtraAmount > 0 && (
                        <>
                          <Grid item xs={6}><Typography variant="body2">Shipping Extra:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2" align="right">${totals.shippingExtraAmount.toFixed(2)}</Typography></Grid>
                        </>
                      )}
                      {totals.warrantyExtraAmount > 0 && (
                        <>
                          <Grid item xs={6}><Typography variant="body2">Warranty Extra:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2" align="right">${totals.warrantyExtraAmount.toFixed(2)}</Typography></Grid>
                        </>
                      )}
                      {totals.customFeeAmount > 0 && (
                        <>
                          <Grid item xs={6}><Typography variant="body2">{customFeeLabel || 'Custom Fee'}:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2" align="right">${totals.customFeeAmount.toFixed(2)}</Typography></Grid>
                        </>
                      )}
                      <Grid item xs={6}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total:</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body1" sx={{ fontWeight: 'bold' }} align="right">${totals.totalAmount.toFixed(2)}</Typography></Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
          
          {/* Payment and Delivery */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Payment & Delivery</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select label="Payment Method" value={payment.method || 'CASH'} onChange={(e) => setPayment({...payment, method: e.target.value})} fullWidth SelectProps={{ native: true }}>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Credit/Debit Card</option>
                  <option value="CHECK">Check</option>
                  <option value="ACIMA">Acima Financing</option>
                  <option value="SYNCHRONY">Synchrony Financing</option>
                  <option value="SNAP">Snap Finance</option>
                  <option value="PROGRESSIVE">Progressive Leasing</option>
                  <option value="LAYAWAY">Layaway</option>
                  <option value="OTHER">Other</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Order Status" value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} fullWidth SelectProps={{ native: true }}>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="ITEMS_ORDERED">Items Ordered</option>
                  <option value="ITEMS_ARRIVED">Items Arrived</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="COMPLETE">Complete</option>
                  <option value="CANCELLED">Cancelled</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Payment Status" value={payment.status || 'pending'} onChange={(e) => {
                  setPayment({...payment, status: e.target.value});
                  // Reset checkbox when status changes
                  if (e.target.value !== 'paid') {
                    setConfirmFullPayment(false);
                  }
                }} fullWidth SelectProps={{ native: true }}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </TextField>
              </Grid>
              {/* Record Payment Section */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Payment Summary
                  </Typography>
                  {/* Payment Summary Display */}
                  <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.totalAmount)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(paidAmount)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: (totals.totalAmount - paidAmount) > 0 ? 'error.main' : 'success.main' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.max(0, totals.totalAmount - paidAmount))}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Record New Payment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Payment Amount"
                        type="number"
                        value={recordPaymentAmount}
                        onChange={(e) => setRecordPaymentAmount(e.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        placeholder={(totals.totalAmount - paidAmount).toFixed(2)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Payment Reference/Notes"
                        value={recordPaymentReference}
                        onChange={(e) => setRecordPaymentReference(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., Check #1234, Card ending 5678"
                      />
                    </Grid>
                  </Grid>
                  {payment.status === 'paid' && (
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={confirmFullPayment}
                            onChange={(e) => setConfirmFullPayment(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={`Mark as fully paid (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.totalAmount)})`}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                        This will set the paid amount equal to the total and clear the balance due
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Delivery Type" value={delivery.type || 'pickup'} onChange={(e) => setDelivery({...delivery, type: e.target.value})} fullWidth SelectProps={{ native: true }}>
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                  <option value="shipping">Shipping</option>
                </TextField>
              </Grid>
              {(delivery.type === 'delivery' || delivery.type === 'shipping') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label={delivery.type === 'delivery' ? 'Select Delivery Option' : 'Select Shipping Option'}
                    value={selectedShippingTitle}
                    onChange={(e) => setSelectedShippingTitle(e.target.value)}
                    fullWidth
                    SelectProps={{ native: true }}
                  >
                    <option value="">None</option>
                    {pricingOptions.shipping.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title} (+${Number(s.cost || 0).toFixed(2)})
                      </option>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Select Warranty Option"
                  value={selectedWarrantyTitle}
                  onChange={(e) => setSelectedWarrantyTitle(e.target.value)}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="">None</option>
                  {pricingOptions.warranties.map((w) => (
                    <option key={w.title} value={w.title}>
                      {w.title} (+${Number(w.cost || 0).toFixed(2)})
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Delivery Notes" value={delivery.notes || ''} onChange={(e) => setDelivery({...delivery, notes: e.target.value})} fullWidth />
              </Grid>

              {/* Custom Fee */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Fee Label"
                  value={customFeeLabel}
                  onChange={(e) => setCustomFeeLabel(e.target.value)}
                  fullWidth
                  placeholder="e.g., Assembly Fee, Setup Fee"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Fee Amount"
                  type="number"
                  value={customFee}
                  onChange={(e) => setCustomFee(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Additional Notes */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Additional Notes</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Order Notes" multiline rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Internal Notes" multiline rows={2} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} fullWidth />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={items.length === 0}>Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderModal;
